import React from 'react';
import { FlatList, ActivityIndicator } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@/lib/styling';
import { Theme } from '@/theme';
import { Box, Text } from '@/components/ui/primitives';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { AppSelect } from '@/components/ui/AppSelect';
import { Switch } from '@/components/ui/Switch';
import { useTRPC } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/AlertDialog';
import { Shuffle } from 'lucide-react-native';

import GameCard from '@/components/GameCard';
import { useLeagueContext } from '@/contexts/LeagueContext';
import AppHeader from '@/components/AppHeader';
import NativeTabFocusGate from '@/components/navigation/NativeTabFocusGate';
import WeekStatusBanner from '@/components/WeekStatusBanner';

type LeagueMember = {
  users: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  };
};

export default function LeaguePicksRoute() {
  const theme = useTheme<Theme>();
  const context = useLeagueContext();
  const { user } = useAuth();
  const trpc = useTRPC();

  const {
    id,
    resolvedSeasonId,
    league,
    leagueScoringSystem,
    seasonWeekMode,
    pickerWeekOptions,
    activeWeek,
    handleWeekChange,
    pickerSelectedValue,
    isSaving,
    dirtyTiebreakerKeys,
    setDirtyTiebreakerKeys,
    completedPicks,
    totalPickTargets,
    clearablePicks,
    availableForRandomPicks,
    clearPicksMutation,
    randomPicksMutation,
    handleRandomPicks,
    initialPicksLoading,
    sortedGames,
    gamesLoading,
    userPicksByBetId,
    picksQueryKey,
  } = context;

  const canUseAdminMode = Boolean(context.permissions?.isOwner || context.permissions?.isManager);
  const [adminMode, setAdminMode] = React.useState(false);
  const [adminUserId, setAdminUserId] = React.useState('');

  const { data: leagueMembers = [], isLoading: membersLoading } = useQuery<LeagueMember[]>(
    trpc.leagues.members.queryOptions(
      { leagueId: id ?? '', seasonId: resolvedSeasonId ?? undefined },
      { enabled: canUseAdminMode && !!id && !!resolvedSeasonId, staleTime: 60_000 },
    ) as any,
  );

  const adminMemberOptions = React.useMemo(
    () =>
      leagueMembers
        .map((member) => {
          const firstName = member.users.firstName?.trim() ?? '';
          const lastName = member.users.lastName?.trim() ?? '';
          const displayName = `${firstName} ${lastName}`.trim() || member.users.email || 'Unknown member';
          return { value: member.users.id, label: displayName };
        })
        .filter((option) => Boolean(option.value)),
    [leagueMembers],
  );

  React.useEffect(() => {
    if (!canUseAdminMode) {
      setAdminMode(false);
      setAdminUserId('');
      return;
    }
    if (adminMemberOptions.length === 0) return;

    const preferredUserId = user?.id ?? '';
    setAdminUserId((current) => {
      if (current && adminMemberOptions.some((option) => option.value === current)) return current;
      return adminMemberOptions.some((option) => option.value === preferredUserId)
        ? preferredUserId
        : adminMemberOptions[0]!.value;
    });
  }, [adminMemberOptions, canUseAdminMode, user?.id]);

  const adminUserPicksInput = {
    leagueId: id,
    userId: adminUserId,
    weekId: activeWeek?.id ?? '',
    ...(resolvedSeasonId ? { seasonId: resolvedSeasonId } : {}),
  };
  const adminPicksQueryKey = trpc.picks.userPicks.queryKey(adminUserPicksInput);
  const { data: adminUserPicks, isLoading: adminUserPicksLoading } = useQuery(
    trpc.picks.userPicks.queryOptions(adminUserPicksInput, {
      enabled: canUseAdminMode && adminMode && !!adminUserId && !!activeWeek?.id && !!resolvedSeasonId,
    }),
  );

  const displayedPicksByBetId = React.useMemo(() => {
    if (!adminMode) return userPicksByBetId;
    const map = new Map<string, { pick: any; game: any }>();
    (adminUserPicks ?? []).forEach((item: any) => {
      const pick = item.picks || item;
      const betKey = pick.leagueWeekBetId ?? `${pick.gameId}-${pick.betType ?? 'moneyline'}`;
      map.set(betKey, { pick, game: item.nfl_games });
    });
    return map;
  }, [adminMode, adminUserPicks, userPicksByBetId]);

  const displayedPicksLoading = initialPicksLoading || (adminMode && adminUserPicksLoading);
  const displayedCompletedPicks = adminMode ? adminUserPicks?.length ?? 0 : completedPicks;
  const displayedPicksQueryKey = adminMode ? adminPicksQueryKey : picksQueryKey;
  React.useEffect(() => {
    setDirtyTiebreakerKeys(new Set());
  }, [adminMode, adminUserId, setDirtyTiebreakerKeys]);

  const headerTitle = league?.name ?? 'League';
  const isRoundPropBet = React.useCallback(
    (bet: any) => Boolean(bet?.displayInfo && typeof bet.displayInfo === 'object' && bet.displayInfo.roundProp === true),
    [],
  );
  const isPropOfTheWeekBet = React.useCallback(
    (bet: any) => bet?.isPropOfTheWeek === true,
    [],
  );
  const roundPropEntries = React.useMemo(() => {
    const entries: any[] = [];
    for (const group of sortedGames) {
      for (const bet of group.bets ?? []) {
        if (!isRoundPropBet(bet)) {
          continue;
        }
        const betKey = bet.leagueWeekBetId ?? `${group.game.id}-${bet.betType}`;
        const existing = displayedPicksByBetId.get(betKey);
        entries.push({
          bet,
          betKey,
          selectedTeamId: existing?.pick?.selectedTeamId,
          pickId: existing?.pick?.id,
          pick: existing?.pick,
          side: existing?.pick?.side,
        });
      }
    }
    return entries;
  }, [displayedPicksByBetId, isRoundPropBet, sortedGames]);
  const roundPropAnchorGame = React.useMemo(
    () => sortedGames.find((group: any) => group.bets?.some((bet: any) => isRoundPropBet(bet)))?.game ?? null,
    [isRoundPropBet, sortedGames],
  );
  const propOfTheWeekEntry = React.useMemo(() => {
    for (const group of sortedGames) {
      const bet = (group.bets ?? []).find((candidate: any) => isPropOfTheWeekBet(candidate));
      if (!bet) {
        continue;
      }
      const betKey = bet.leagueWeekBetId ?? `${group.game.id}-${bet.betType}`;
      const existing = displayedPicksByBetId.get(betKey);
      return {
        game: group.game,
        bet,
        betKey,
        selectedTeamId: existing?.pick?.selectedTeamId,
        pickId: existing?.pick?.id,
        pick: existing?.pick,
        side: existing?.pick?.side,
      };
    }
    return null;
  }, [displayedPicksByBetId, isPropOfTheWeekBet, sortedGames]);
  const normalGameGroups = React.useMemo(
    () =>
      sortedGames
        .map((group: any) => ({
          ...group,
          bets: (group.bets ?? []).filter(
            (bet: any) => !isRoundPropBet(bet) && !isPropOfTheWeekBet(bet),
          ),
        }))
        .filter((group: any) => group.bets.length > 0),
    [isPropOfTheWeekBet, isRoundPropBet, sortedGames],
  );
  const handleTiebreakerDirtyChange = React.useCallback((betKey: string, isDirty: boolean) => {
    setDirtyTiebreakerKeys((prev: Set<string>) => {
      const next = new Set(prev);
      if (isDirty) {
        next.add(betKey);
      } else {
        next.delete(betKey);
      }
      return next;
    });
  }, [setDirtyTiebreakerKeys]);
  const renderGameCard = React.useCallback(({ item: group }: { item: any }) => {
    const betEntries =
      group.bets?.map((bet: any) => {
        const betKey = bet.leagueWeekBetId ?? `${group.game.id}-${bet.betType}`;
        const existing = displayedPicksByBetId.get(betKey);
        return {
          bet,
          betKey,
          selectedTeamId: existing?.pick?.selectedTeamId,
          pickId: existing?.pick?.id,
          pick: existing?.pick,
          side: existing?.pick?.side,
        };
      }) ?? [];

    return (
      <Box marginBottom="m">
        <GameCard
          game={group.game}
          betEntries={betEntries}
          leagueId={id}
          weekId={activeWeek?.id ?? null}
          seasonId={resolvedSeasonId}
          pick={betEntries.find((entry: any) => entry.bet.betType === 'total_tiebreaker')?.pick ?? null}
          picksQueryKey={displayedPicksQueryKey}
          adminUserId={adminMode ? adminUserId : undefined}
          scoringSystem={leagueScoringSystem}
          seasonWeekMode={seasonWeekMode as any}
          onTiebreakerDirtyChange={handleTiebreakerDirtyChange}
        />
      </Box>
    );
  }, [activeWeek?.id, adminMode, adminUserId, displayedPicksByBetId, displayedPicksQueryKey, handleTiebreakerDirtyChange, id, leagueScoringSystem, resolvedSeasonId, seasonWeekMode]);

  return (
    <NativeTabFocusGate>
      <Box flex={1} backgroundColor="background" textured={false}>
        <AppHeader title={headerTitle} currentLeagueId={id} allowBack={false} />
        <FlatList
          data={gamesLoading || displayedPicksLoading ? [] : normalGameGroups}
          renderItem={renderGameCard}
          keyExtractor={(group) => group.game.id}
          initialNumToRender={4}
          maxToRenderPerBatch={4}
          updateCellsBatchingPeriod={50}
          windowSize={5}
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          ListHeaderComponent={
            <>
          <WeekStatusBanner
            sortedGames={sortedGames}
            isComplete={!adminMode && totalPickTargets > 0 && completedPicks >= totalPickTargets}
          />
        
        <Box paddingVertical="s">
          <Box marginBottom="m">
            <Box flexDirection="row" alignItems="center" style={styles.controlsRow}>
              <Box style={styles.weekPicker}>
                <AppSelect
                  value={pickerSelectedValue}
                  onChange={(value) => {
                    if (value) {
                      handleWeekChange(value, 'picks');
                    }
                  }}
                  options={
                    pickerWeekOptions.length === 0
                      ? [{ value: 'no-weeks', label: 'No weeks', disabled: true }]
                      : pickerWeekOptions.map((week) => ({
                          value: week.id,
                          label: week.displayLabel,
                        }))
                  }
                  placeholder={pickerWeekOptions.length === 0 ? 'No weeks' : 'Select week'}
                  simplifiedTrigger
                />
              </Box>

              <Box flexDirection="row" alignItems="center" style={styles.toolbarMeta}>
                <Box flexDirection="row" alignItems="center" style={styles.savedSummary}>
                  {isSaving > 0 ? (
                    <>
                      <ActivityIndicator size="small" color={theme.colors.mutedForeground} />
                      <Text variant="caption" color="mutedForeground" style={styles.savedSummaryText}>
                        Saving
                      </Text>
                    </>
                  ) : dirtyTiebreakerKeys.size > 0 ? (
                    <Text variant="caption" color="warning" style={styles.savedSummaryText}>
                      Not saved
                    </Text>
                  ) : (
                    <Text variant="caption" color="success" style={styles.savedSummaryText}>
                      {displayedCompletedPicks}/{totalPickTargets} SAVED
                    </Text>
                  )}
                </Box>
                {!adminMode && (clearablePicks.length > 0 || availableForRandomPicks.length > 0) ? (
                  <Box style={styles.toolbarDivider} />
                ) : null}
                {!adminMode && clearablePicks.length > 0 ? (
                  <AlertDialog>
                    <AlertDialogTrigger>
                      <Button
                        accessibilityLabel="Clear all available picks"
                        disabled={clearPicksMutation.isPending}
                        variant="ghost"
                        style={styles.compactButton}
                        size="sm"
                      >
                        <Box flexDirection="row" alignItems="center" justifyContent="center" style={styles.compactButtonContent}>
                          <Text
                            variant="caption"
                            fontWeight="bold"
                            color="press"
                            numberOfLines={1}
                          >
                            {clearPicksMutation.isPending ? '...' : 'CLEAR'}
                          </Text>
                        </Box>
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Clear All Picks?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will delete all your picks for games that have not started yet.
                          This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => clearPicksMutation.mutate(clearablePicks.map((entry: any) => entry.picks.id))}
                          variant="destructive"
                        >
                          Clear Picks
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : (
                  !adminMode && availableForRandomPicks.length > 0 && (
                    <Button
                      accessibilityLabel="Make random picks"
                      variant="outline"
                      onPress={handleRandomPicks}
                      disabled={!resolvedSeasonId || randomPicksMutation.isPending}
                      style={styles.compactButton}
                      size="sm"
                    >
                      <Box flexDirection="row" alignItems="center" justifyContent="center" style={styles.compactButtonContent}>
                        <Shuffle size={14} color={theme.colors.press} />
                        <Text variant="caption" color="press" numberOfLines={1}>
                          {randomPicksMutation.isPending ? '...' : 'RND'}
                        </Text>
                      </Box>
                    </Button>
                  )
                )}
              </Box>
            </Box>
          </Box>

          {canUseAdminMode ? (
            <Box style={styles.adminModePanel}>
              <Box flexDirection="row" alignItems="center" justifyContent="space-between">
                <Box flex={1} marginRight="m">
                  <Text variant="title" style={styles.adminModeTitle}>
                    Admin Mode
                  </Text>
                  <Text variant="caption" color="mutedForeground" style={styles.adminModeDescription} numberOfLines={1}>
                    Make picks for any league member
                  </Text>
                </Box>
                <Switch
                  accessibilityLabel="Toggle Admin Mode"
                  checked={adminMode}
                  onCheckedChange={setAdminMode}
                  disabled={membersLoading || adminMemberOptions.length === 0}
                />
              </Box>
              {adminMode ? (
                <Box marginTop="s">
                  <Text variant="caption" color="press" style={styles.adminFieldLabel}>
                    Picking for
                  </Text>
                  <AppSelect
                    value={adminUserId}
                    onChange={setAdminUserId}
                    options={adminMemberOptions}
                    placeholder={membersLoading ? 'Loading members...' : 'Select a member'}
                    disabled={membersLoading || adminMemberOptions.length === 0}
                    triggerStyle={styles.adminUserSelect}
                  />
                </Box>
              ) : null}
            </Box>
          ) : null}

          {/* Loading */}
          {(() => {
            if (displayedPicksLoading) {
              return (
                <Box marginBottom="m" flexDirection="row" alignItems="center" gap="s" borderBottomWidth={3} borderColor="press" paddingBottom="m">
                  <ActivityIndicator size="small" color={theme.colors.press} />
                  <Text variant="caption" color="pressMuted">Loading your picks...</Text>
                </Box>
              );
            }

            if (sortedGames.length === 0) return null;

            return null;
          })()}

          {/* Games List */}
          {gamesLoading || displayedPicksLoading ? (
            <Box gap="m">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} style={styles.gameSkeleton} />
              ))}
            </Box>
          ) : sortedGames.length > 0 ? (
            <Box gap="m">
              {propOfTheWeekEntry ? (
                <GameCard
                  key="prop-bet-of-the-week"
                  game={propOfTheWeekEntry.game}
                  betEntries={[propOfTheWeekEntry]}
                  cardTitle="PCK of the Week"
                  hideGameTeams
                  propsSectionTitle="Make Your Pick"
                  propsInitiallyExpanded
                  isFeatured
                  leagueId={id}
                  weekId={activeWeek?.id ?? null}
                  seasonId={resolvedSeasonId}
                  pick={null}
                  picksQueryKey={displayedPicksQueryKey}
                  adminUserId={adminMode ? adminUserId : undefined}
                  scoringSystem={leagueScoringSystem}
                  seasonWeekMode={seasonWeekMode as any}
                  onTiebreakerDirtyChange={handleTiebreakerDirtyChange}
                />
              ) : null}
              {roundPropAnchorGame && roundPropEntries.length > 0 ? (
                <GameCard
                  key="round-prop-bets"
                  game={roundPropAnchorGame}
                  betEntries={roundPropEntries}
                  cardTitle="Round Prop Bets"
                  hideGameTeams
                  propsSectionTitle="Round Prop Bets"
                  leagueId={id}
                  weekId={activeWeek?.id ?? null}
                  seasonId={resolvedSeasonId}
                  pick={null}
                  picksQueryKey={displayedPicksQueryKey}
                  adminUserId={adminMode ? adminUserId : undefined}
                  scoringSystem={leagueScoringSystem}
                  seasonWeekMode={seasonWeekMode as any}
                  onTiebreakerDirtyChange={handleTiebreakerDirtyChange}
                />
              ) : null}
            </Box>
          ) : (
            <Box alignItems="center" paddingVertical="xl">
              <Text variant="body" color="mutedForeground">
                No games available for this week
              </Text>
            </Box>
          )}
        </Box>
            </>
          }
        />
      </Box>
    </NativeTabFocusGate>
  );
}

const styles = StyleSheet.create(theme => ({
  adminFieldLabel: {
    marginBottom: theme.spacing.xs,
  },
  adminModeDescription: {
    fontFamily: theme.typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.35,
    lineHeight: 18,
    textTransform: 'uppercase',
  },
  adminModePanel: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.border,
    borderRadius: theme.borderRadii.m,
    borderWidth: 1,
    elevation: 0,
    marginBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.s,
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  adminModeTitle: {
    fontSize: 18,
    lineHeight: 22,
  },
  adminUserSelect: {
    width: '100%',
  },
  compactButton: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    elevation: 0,
    flexShrink: 0,
    height: 34,
    paddingHorizontal: 6,
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  compactButtonContent: {
    columnGap: 4,
  },
  controlsRow: {
    borderBottomColor: theme.colors.pressMuted,
    borderBottomWidth: 1,
    columnGap: 6,
    paddingBottom: theme.spacing.s,
    width: '100%',
  },
  gameSkeleton: {
    height: 88,
    marginBottom: theme.spacing.s,
  },
  savedSummary: {
    flexShrink: 0,
  },
  savedSummaryText: {
    fontFamily: theme.typography.fontFamily.display,
    fontSize: 18,
    letterSpacing: -0.25,
    textTransform: 'uppercase',
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: theme.spacing.m,
    paddingBottom: 104,
  },
  toolbarDivider: {
    backgroundColor: theme.colors.pressMuted,
    height: 24,
    width: 1,
  },
  toolbarMeta: {
    columnGap: 6,
    flexShrink: 0,
    justifyContent: 'flex-end',
  },
  weekPicker: {
    flex: 1,
    minWidth: 0,
  },
}));
